import { api } from './index';

import type { Recipient, Survey, SurveyTemplate } from '@/types';

export interface GetSurveyTemplateParams {
  templateId: string;
}

export interface GetSurveysParams {
  templateId?: string;
  userId: string;
}

export interface GetSurveyTemplatesParams {
  userId?: string;
}

export interface CreateSurveyParams {
  surveyTemplateId: string;
  assignedParticipants: Recipient[];
}

export interface SendSurveyParams {
  surveyId: string;
  emailTemplate: string;
}

export interface ShareSurveyParams {
  surveyId: string;
}

export interface UpdateParticipantsParams {
  surveyId: string;
  assignedParticipants: Recipient[];
}

export interface DeliverSurveyParams {
  surveyId: string;
  emailTemplate: string;
  recipients: Recipient[];
}

export const surveyApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get survey template by ID
    getSurveyTemplate: builder.query<SurveyTemplate, GetSurveyTemplateParams>({
      providesTags: (_, __, { templateId }) => [
        { id: templateId, type: 'SurveyTemplate' },
        'SurveyTemplate',
      ],
      query: ({ templateId }) => ({
        method: 'GET',
        url: `/survey_templates/${templateId}`,
      }),
    }),

    // Get survey templates list
    getSurveyTemplates: builder.query<
      SurveyTemplate[],
      GetSurveyTemplatesParams
    >({
      providesTags: ['SurveyTemplate'],
      query: ({ userId }) => {
        const params: Record<string, string | undefined> = {};
        if (userId) params.userId = userId;
        return {
          method: 'GET',
          params,
          url: '/survey_templates',
        };
      },
    }),

    // Get surveys with optional filters
    getSurveys: builder.query<Survey[], GetSurveysParams>({
      providesTags: ['Survey'],
      query: ({ templateId, userId }) => {
        const params: Record<string, string | undefined> = {};
        if (templateId) params.templateId = templateId;
        params.userId = userId;
        return {
          method: 'GET',
          params,
          url: '/surveys',
        };
      },
    }),

    // Create survey
    createSurvey: builder.mutation<Survey, CreateSurveyParams>({
      invalidatesTags: ['Survey'],
      query: ({ surveyTemplateId, assignedParticipants }) => ({
        data: { surveyTemplateId, assignedParticipants },
        method: 'POST',
        url: '/surveys',
      }),
    }),

    // Send survey
    sendSurvey: builder.mutation<void, SendSurveyParams>({
      invalidatesTags: (_result, _error, { surveyId }) => [
        { id: surveyId, type: 'Survey' },
        'Survey',
      ],
      query: ({ surveyId, emailTemplate }) => ({
        data: { emailTemplate },
        method: 'POST',
        url: `/surveys/${surveyId}/send_emails`,
      }),
    }),

    // Deliver (add recipients and send emails in one call)
    deliverSurvey: builder.mutation<void, DeliverSurveyParams>({
      invalidatesTags: (_result, _error, { surveyId }) => [
        { id: surveyId, type: 'Survey' },
        'Survey',
      ],
      query: ({ surveyId, emailTemplate, recipients }) => ({
        data: { email_template: emailTemplate, recipients },
        method: 'POST',
        url: `/surveys/${surveyId}/deliver`,
      }),
    }),

    // Share survey
    shareSurvey: builder.mutation<Survey, ShareSurveyParams>({
      invalidatesTags: (_result, _error, { surveyId }) => [
        { id: surveyId, type: 'Survey' },
        'Survey',
      ],
      query: ({ surveyId }) => ({
        data: { status: 'SHARED' },
        method: 'PATCH',
        url: `/surveys/${surveyId}`,
      }),
    }),

    // Update participants
    updateParticipants: builder.mutation<Survey, UpdateParticipantsParams>({
      invalidatesTags: (_result, _error, { surveyId }) => [
        { id: surveyId, type: 'Survey' },
        'Survey',
      ],
      query: ({ surveyId, assignedParticipants }) => ({
        data: { assignedParticipants },
        method: 'PATCH',
        url: `/surveys/${surveyId}`,
      }),
    }),
  }),
});

export const {
  useGetSurveyTemplateQuery,
  useGetSurveyTemplatesQuery,
  useGetSurveysQuery,
  useCreateSurveyMutation,
  useSendSurveyMutation,
  useDeliverSurveyMutation,
  useShareSurveyMutation,
  useUpdateParticipantsMutation,
} = surveyApi;
